import React, { useEffect, useMemo, useState } from 'react'
import type { MouseEvent, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { ListItemDark } from 'flipper-ui'
import SidebarNew from 'flipper-ui/core/navigation/sidebar-new'
import {
    IconAssignment,
    IconCheckBox,
    IconDateRange,
    IconHome
} from 'flipper-ui/icons/mui'
import { IconIPE } from 'flipper-ui/icons/specific/IconIPE'
import { IconRisks } from 'flipper-ui/icons/specific/IconRisks'
import type { TFunction } from 'i18next'
import { useDesignVersion } from '@core/layout/design-version/useDesignVersion'
import logoSidebar from '@public/images/logo-white.svg'
import { SidebarLogo, SidebarMainList } from './styles'

interface ISidebarOption {
    icon: ReactElement
    label: string
    name: string
    route: string
}

const getOptions = (t: TFunction): ISidebarOption[] => [
    {
        icon: <IconHome />,
        label: t('common:home'),
        name: 'home',
        route: '/home'
    },
    {
        icon: <IconDateRange />,
        label: t('portal:consultations:moduleLabel'),
        name: 'consultations',
        route: '/consultations'
    },
    {
        icon: <IconIPE width={24} height={24} />,
        label: t('portal:ppe:moduleLabel'),
        name: 'ppe',
        route: '/epis'
    },
    {
        icon: <IconRisks width={24} height={24} />,
        label: t('portal:risks:moduleLabel'),
        name: 'risks',
        route: '/risks'
    },
    {
        icon: <IconCheckBox />,
        label: t('portal:safetyQuiz:moduleLabel'),
        name: 'safety-questionnaires',
        route: '/safety-questionnaires'
    },
    {
        icon: <IconAssignment />,
        label: t('portal:anamnesis:moduleLabel'),
        name: 'clinical-questionnaires',
        route: '/clinical-questionnaires'
    }
]

const SidebarV2 = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const { expandedSidebar, setExpandedSidebar } = useDesignVersion()

    const [selected, setSelected] = useState<string>('')
    const [expanded, setExpanded] = useState<boolean>(expandedSidebar)

    const options = useMemo(() => getOptions(t), [t])

    useEffect(() => {
        const matched = options.find(
            option =>
                location.pathname === option.route ||
                location.pathname.includes(option.name)
        )

        if (matched && matched.name !== selected) {
            setSelected(matched.name)
        }
    }, [location.pathname, options, selected])

    const handleToggleSidebar = () => {
        const next = !expanded

        setExpanded(next)
        setExpandedSidebar(next)
    }

    const handleGoTo =
        (name: string, route: string) =>
        (event?: MouseEvent<HTMLDivElement> | undefined) => {
            event?.preventDefault()
            event?.stopPropagation()
            setSelected(name)
            navigate(route)
        }

    const renderOption = (option: ISidebarOption) => (
        <ListItemDark
            key={option.name}
            icon={option.icon}
            selected={option.name === selected}
            href={option.route}
            title={expanded ? option.label : ''}
            expanded={expanded}
            onClick={handleGoTo(option.name, option.route)}
        />
    )

    return (
        <SidebarNew
            open
            docked
            top={0}
            name='sidebar-v2'
            expanded={expanded}
            logo={
                <SidebarLogo
                    alt='Portal'
                    src={logoSidebar}
                    onClick={() => navigate('/home')}
                />
            }
            onToggle={handleToggleSidebar}>
            <SidebarMainList>{options.map(renderOption)}</SidebarMainList>
        </SidebarNew>
    )
}

export default SidebarV2
